import { X, Download, Share2, GraduationCap, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { useToast } from "@/hooks/use-toast";

interface CompletionCertificateProps {
  username: string;
  completedAt: string;
  totalLessons: number;
  onClose: () => void;
}

export const CompletionCertificate = ({
  username,
  completedAt,
  totalLessons,
  onClose,
}: CompletionCertificateProps) => {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const lang = i18n.language.split("-")[0];

  const formattedDate = new Date(completedAt).toLocaleDateString(
    lang === "es" ? "es-ES" : "en-US",
    { year: "numeric", month: "long", day: "numeric" },
  );

  const handleShare = () => {
    const shareText =
      lang === "es"
        ? `He completado los 7 niveles de la Academia de Poker de SmartPoker! ${totalLessons} lecciones terminadas. smartpoker.app`
        : `I've completed all 7 levels of the SmartPoker Poker Academy! ${totalLessons} lessons finished. smartpoker.app`;

    navigator.clipboard.writeText(shareText).then(() => {
      toast({
        title: lang === "es" ? "Copiado al portapapeles" : "Copied to clipboard",
        description:
          lang === "es"
            ? "Texto para compartir copiado"
            : "Share text copied",
      });
    });
  };

  const handleDownload = () => {
    toast({
      title: lang === "es" ? "Proximamente" : "Coming soon",
      description:
        lang === "es"
          ? "La descarga estara disponible pronto"
          : "Download will be available soon",
    });
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-6 right-6 text-white/60 hover:text-white transition-colors z-10"
      >
        <X className="w-6 h-6" />
      </button>

      {/* Certificate card */}
      <div className="w-full max-w-sm glass border-2 border-secondary/50 rounded-2xl p-6 space-y-5 relative overflow-hidden">
        {/* Decorative corner elements */}
        <div className="absolute top-0 left-0 w-16 h-16 border-t-2 border-l-2 border-secondary/30 rounded-tl-2xl" />
        <div className="absolute top-0 right-0 w-16 h-16 border-t-2 border-r-2 border-secondary/30 rounded-tr-2xl" />
        <div className="absolute bottom-0 left-0 w-16 h-16 border-b-2 border-l-2 border-secondary/30 rounded-bl-2xl" />
        <div className="absolute bottom-0 right-0 w-16 h-16 border-b-2 border-r-2 border-secondary/30 rounded-br-2xl" />

        {/* Logo */}
        <div className="flex justify-center">
          <div className="bg-secondary/20 p-3 rounded-full">
            <GraduationCap className="w-8 h-8 text-secondary" />
          </div>
        </div>

        {/* SmartPoker branding */}
        <p className="text-center text-xs font-semibold text-secondary uppercase tracking-widest">
          SmartPoker
        </p>

        {/* Title */}
        <h2 className="text-center text-lg font-bold">
          {t("certificate_title")}
        </h2>

        {/* Username */}
        <p className="text-center text-2xl font-bold text-secondary">
          {username}
        </p>

        {/* Description */}
        <p className="text-center text-sm text-muted-foreground leading-relaxed">
          {t("certificate_desc")}
        </p>

        {/* Stats */}
        <div className="flex justify-center">
          <div className="glass px-4 py-2 flex items-center gap-2">
            <Award className="w-4 h-4 text-secondary" />
            <span className="text-sm font-semibold">
              {totalLessons}{" "}
              {lang === "es" ? "lecciones" : "lessons"}
            </span>
          </div>
        </div>

        {/* Date */}
        <p className="text-center text-xs text-muted-foreground">
          {t("certificate_date", { date: formattedDate })}
        </p>

        {/* Action buttons */}
        <div className="flex gap-3 pt-2">
          <Button
            variant="outline"
            className="flex-1 border-secondary/30 text-secondary hover:bg-secondary/10"
            onClick={handleDownload}
          >
            <Download className="w-4 h-4 mr-1.5" />
            {t("certificate_download")}
          </Button>
          <Button
            className="flex-1 bg-secondary hover:bg-secondary/90 text-secondary-foreground"
            onClick={handleShare}
          >
            <Share2 className="w-4 h-4 mr-1.5" />
            {t("certificate_share")}
          </Button>
        </div>
      </div>
    </div>
  );
};
